<script setup lang="ts">
// A value that must match something the node knows (a sound card, an NDI
// source, a display mode) picked from what it found, never typed (Niels,
// DECISIONS 2026-10-06 "pick, not type"). A saved value that is not found
// now stays, marked; `other` allows typing one behind an explicit choice,
// for what discovery cannot see.
import { computed, ref, watch } from 'vue'

defineOptions({ inheritAttrs: false }) // data-test and the like go on the select

const model = defineModel<string | undefined>({ required: true })
const props = defineProps<{
    options: { value: string; label?: string }[]
    empty?: string // the label of '' (no choice: the default); none: '' is not offered
    missing?: string // how a saved value not found now is marked
    other?: string // offer typing one, under this label ("type a name…")
    placeholder?: string // the typed field's
    looking?: boolean // still being looked for: the first option says so
}>()
const OTHER = '\u0000other'
const found = computed(() => props.options.some((o) => o.value === model.value))
// typing: chosen, or a saved value that is not in the list when typing is allowed
const typing = ref(!!props.other && !!model.value && !found.value)
watch(found, (f) => {
    if (f) typing.value = false
})
const choice = computed(() => (typing.value ? OTHER : (model.value ?? '')))
function pick(v: string) {
    if (v === OTHER) {
        typing.value = true
        return
    }
    typing.value = false
    model.value = v
}
</script>

<template>
    <div class="flex min-w-0 gap-2">
        <select
            class="min-w-0 flex-1"
            :value="choice"
            v-bind="$attrs"
            @change="pick(($event.target as HTMLSelectElement).value)"
        >
            <option v-if="empty !== undefined" value="">{{ empty }}</option>
            <option v-else-if="!model && !typing" value="" disabled>
                {{ looking ? 'looking…' : options.length ? '— pick one —' : 'none found' }}
            </option>
            <option v-for="o in options" :key="o.value" :value="o.value">{{ o.label ?? o.value }}</option>
            <option v-if="model && !found && !typing" :value="model">
                {{ model }} ({{ missing ?? 'not on this node now' }})
            </option>
            <option v-if="other" :value="OTHER">{{ other }}</option>
        </select>
        <input
            v-if="typing"
            v-model="model"
            class="min-w-0 flex-1 font-mono"
            :placeholder="placeholder"
            data-test="pick-typed"
        />
    </div>
</template>
